/**
 * ADMIN GNB(사이드바) 메뉴 정의 — 단일 원천.
 *
 * 메뉴 증감은 이 배열만 수정하면 사이드바(widgets/admin-sidebar)와
 * 준비중 페이지 라우트(app/routes)에 함께 반영된다.
 * 실제 페이지가 생기면 app/routes 에서 해당 경로만 실제 페이지로 승격한다.
 */
import {
  BellDot,
  BellRing,
  Building2,
  CreditCard,
  Database,
  Factory,
  FileClock,
  FileText,
  GalleryHorizontal,
  Image,
  LayoutDashboard,
  type LucideIcon,
  Megaphone,
  MessageSquareText,
  MessageSquareWarning,
  Package,
  PackagePlus,
  Settings,
  ShoppingCart,
  Truck,
  Users,
} from 'lucide-react';

export interface AdminGnbItem {
  readonly label: string;
  readonly path: string;
  readonly icon: LucideIcon;
  /** 다른 메뉴가 이 경로 하위에 있으면 true — NavLink 가 정확 일치로만 활성화된다. */
  readonly end?: boolean;
}

export interface AdminGnbSection {
  readonly label: string;
  readonly items: readonly AdminGnbItem[];
}

/** 루트(/) 진입 시 이동할 기본 경로. */
export const DEFAULT_PATH = '/dashboard';

export const ADMIN_GNB_SECTIONS: readonly AdminGnbSection[] = [
  {
    label: '대시보드',
    items: [{ label: '대시보드', path: '/dashboard', icon: LayoutDashboard }],
  },
  {
    label: '상품 및 가격',
    items: [
      { label: '상품 목록', path: '/products', icon: Package, end: true },
      { label: '상품 등록 및 수정', path: '/products/editor', icon: PackagePlus },
      { label: '기준 정보 관리', path: '/master-data', icon: Database },
      { label: '기획전 관리', path: '/exhibitions', icon: Megaphone },
      { label: '포트폴리오 관리', path: '/portfolio', icon: Image },
      { label: '후기 관리', path: '/reviews', icon: MessageSquareText },
    ],
  },
  {
    // 비회원 상담 요청도 견적 목록(비회원 배지)에서 처리한다(2026-10-16 결정) — 별도 상담 접수함은 두지 않는다.
    label: '상담 및 견적',
    items: [
      // 견적 요청 = 처리할 건(검토 대기·수정 요청), 견적 내역 = 그 나머지(발행 뒤·끝난 건). 2026-10-20 결정.
      { label: '견적 요청', path: '/quotes', icon: FileText, end: true },
      { label: '견적 내역', path: '/quotes/history', icon: FileClock },
    ],
  },
  {
    label: '주문 및 제작',
    items: [
      { label: '주문 목록', path: '/orders', icon: ShoppingCart },
      { label: '결제 내역', path: '/payments', icon: CreditCard },
      { label: '교환·환불 문의', path: '/order-inquiries', icon: MessageSquareWarning },
      { label: '제작 발주 자료', path: '/production-orders', icon: Factory },
    ],
  },
  {
    label: '설정',
    items: [
      { label: '회원 관리', path: '/users', icon: Users },
      { label: '랜딩 페이지', path: '/landing', icon: Image },
      { label: '배너 관리', path: '/banners', icon: GalleryHorizontal },
      // 알림(알림 설계 §6). 발송 이력은 알림 설정(/notifications/settings)의 접두사라 정확 일치로만 활성화한다.
      { label: '알림 발송 이력', path: '/notifications', icon: BellRing, end: true },
      { label: '알림 설정', path: '/notifications/settings', icon: BellDot },
      // 사업자 정보·입금 계좌(계좌이체·전자세금계산서 설계 §5-6). 단가 관리 메뉴는 화면이 없어 뺐다 — 필요해지면 다시 넣는다.
      { label: '사업자·입금 계좌', path: '/settings/supplier', icon: Building2 },
      // 배송비(SUPER-86 D9).
      { label: '배송비 설정', path: '/settings/shipping-fee', icon: Truck },
      // 카카오톡 채널 주소(고객용 웹 상담 버튼). docs/superpowers/specs/2026-10-29-kakao-channel-chat-button-design.md
      { label: '카카오톡 채널', path: '/settings/site', icon: Settings },
    ],
  },
];
